import { useMemo, useState } from "react"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { X, Loader, Search } from 'lucide-react'
import { truncateText } from "../../../util/utils"
import { useParams } from "react-router-dom"
import BreadCrumb from "../../../componentsV2/BreadCrumb"
import PageSpinner from "../../../components/PageSpinner"
import { message } from "antd"
import useSWRMutation from "swr/mutation"
import { API_ENDPOINTS } from "../../../util/api"
import { fetcher, handleMutation, handlePatchUpdates } from "../../../util/axiosConfig"
import requestErrorHandler from "../../../util/requestErrorHandler"
import useSWR from "swr"
import PlaceholderCell from "../../../componentsV2/PlaceholderCell"
import { cn } from "../../../lib/utils";

export function MapZonePricing() {
  const { truckId } = useParams();

  const { data: _category, isLoading: isLoadingCategory, mutate: mutateCategory } = useSWR(truckId ? API_ENDPOINTS.TRUCK_CATEGORY.GET_ONE(truckId!) : null, fetcher);

  const category: ZoneTrucks = _category?.truckCategory
  const vehicleCategoryId = category?.id;
  const { data, error, isLoading, mutate } = useSWR(vehicleCategoryId ? API_ENDPOINTS.ZONE_MAPPING.GET_MAPPED_ZONES(vehicleCategoryId) : null, fetcher)

  const mappedZones: MappedZones[] = data?.data
  const zoneKeys: { [key: string]: MappedZones } = {}

  mappedZones?.map(zone => {
    const key = `${zone?.pickupZone?.title.toLowerCase()}${zone?.dropoffZone?.title.toLowerCase()}`
    zoneKeys[key] = zone
    // return({[key]: zone})
  })

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [selectedZone, setSelectedZone] = useState<MappedZones | null>(null)
  const [search, setSearch] = useState("");
  const zones = category?.zones;

  const filteredZones: Zone[] = useMemo(() => {
    if (zones) {
      return zones.filter((zone) => 
        zone.title.toLowerCase().includes(search.toLowerCase())
      )
    }
    return []
  }, [zones, search]);

  const handleCellClick = (cell: MappedZones) => {
    setSelectedZone(cell)
    setSidebarOpen(true)
  }

  if (isLoading || isLoadingCategory) {
    return <PageSpinner />
  }

  return (
    <div className="flex gap-4 flex-nowrap min-h-full overflow-hidden mb-4 transition-all duration-200">
      <div className="flex-1 bg-white rounded-lg shadow p-6 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <BreadCrumb label={`Zones - Prices - ${category?.name}`} showBack />
        </div>

        <div className="mb-6 md:w-60">
          <div className="flex items-center gap-2 border-gray-300 rounded px-2" style={{ border: '1px solid #d1d5db' }}>
            <Search size={18} className="text-gray-300" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by zone"
              className="bg-white shadow-none"
            />
          </div>
        </div>
        <div
          className={cn(
            "grid gap-3 transition-all duration-300",
            "transition-all duration-200 overflow-auto"
          )}
          style={{ gridTemplateColumns: `repeat(${zones?.length}, 1fr)` }}
        >
          {
            filteredZones.map(i_zone => (
              <div key={i_zone.id} className="flex flex-col gap-3">
                {
                  zones?.map(j_zone => {
                    const _key = `${i_zone.title.toLowerCase()}${j_zone.title.toLowerCase()}`
                    const isMapped = Object.keys(zoneKeys).includes(_key)
                    const notMapped: MappedZones = {
                      createdAt: "",
                      dropoffZone: j_zone,
                      dropoffZoneId: j_zone.id,
                      id: "",
                      pickupZone: i_zone,
                      pickupZoneId: i_zone.id,
                      pricePerMaterial: category.materials.map(mat => ({ material: mat.name, price: "" })),
                      updatedAt: "",
                      vehicleCategoryId: "",
                    }
                    const mappedZone = isMapped ? zoneKeys[_key] : notMapped

                    if (i_zone.id === j_zone.id) {
                      return <PlaceholderCell key={_key} />
                    }
                    return (
                      <PriceCell
                        category={category}
                        key={j_zone.id}
                        cell={{ ...mappedZone, pickupZone: i_zone.title, dropoffZone: j_zone.title }}
                        onClick={() => handleCellClick(mappedZone)}
                      />
                    )
                  })
                }
              </div>
            ))
          }
        </div>
      </div>
      {sidebarOpen && (
        <div key={selectedZone?.id} className="bg-white w-1/4 z-10 border-l overflow-auto transition-all duration-300">
          <ZoneSidebar
            category={category}
            mutate={mutate}
            zone={selectedZone}
            onClose={() => setSidebarOpen(false)}
          />
        </div>
      )}
    </div>
  )
}

type CellData = {
  pickupZone: string,
  dropoffZone: string,

} & Omit<MappedZones, "dropoffZone" | "pickupZone">

function PriceCell({ cell, onClick, hasPrice, category }: { cell: CellData, onClick: () => void, hasPrice?: boolean, category?: ZoneTrucks, }) {
  const numOfMaterials = category?.materials?.length;

  const noPricesSet = cell?.pricePerMaterial?.length === 0 
    ? 0
    : cell?.pricePerMaterial?.filter(mat => mat.price && mat.price !== "" && +mat.price > 0).length

  const invalid = noPricesSet! < numOfMaterials!;
  
  const sumPrices = useMemo(() => {
    if (!cell?.pricePerMaterial || cell?.pricePerMaterial.length === 0) return 0;
    return cell.pricePerMaterial.reduce((acc, mat) => {
      const price = parseFloat(mat.price);
      return acc + (isNaN(price) ? 0 : 1);
    }, 0);
  }, [cell?.pricePerMaterial]);

  return (
    <div
      className={cn(
        `min-w-[200px] flex justify-between items-center border-solid border rounded-sm px-2 py-3 h-[64px] cursor-pointer  transition-all hover:opacity-50  ${invalid ? "bg-orange-500/15 border-orange-400 text-orange-500" : "bg-blue-500/15 border-blue-500 text-blue-500"}`
      )}
      onClick={onClick}
    >
      <div className="flex-1 flex flex-col text-xs gap-2 font-bold">
        <span>
          {truncateText(cell.pickupZone.split(' ')[0], 20)}
        </span>
        <span>
          {truncateText(cell.dropoffZone.split(' ')[0], 20)}
        </span>
      </div>
      <div className="bg-black h-full w-[2px] mx-2" />
      <div className="self-end flex flex-col">
        {!invalid ? (
          <div className="text-sm font-semibold">
            {`${sumPrices} / ${numOfMaterials}`}
          </div>
        ) : (
          <div className="text-sm font-bold text-black">
            N.A
          </div>
        )}
        <small className="text-[8px] text-gray-500 font-semibold">Click to edit price</small>
      </div>
    </div>
  )
}


function ZoneSidebar({ zone, onClose, category, mutate }: { zone: MappedZones | null, onClose: () => void, category: ZoneTrucks, mutate: any }) {
  const vehicleCategoryId = category?.id;
  const { trigger, isMutating } = useSWRMutation(API_ENDPOINTS.ZONE_MAPPING.MAP_ZONES, handleMutation);
    const { trigger: updateZone, isMutating: isUpdatingZone } = useSWRMutation(API_ENDPOINTS.ZONE_MAPPING.UPDATE, handlePatchUpdates);

  const [pickupZoneId, setPickupZoneId] = useState(zone?.pickupZoneId)
  const [dropOffZoneId, setDropOffZoneId] = useState(zone?.dropoffZoneId)
  const [materialPrice, setMaterialPrice] = useState<MaterialPrice[]>([])

  const materialsList: { material: string, price: string }[] = useMemo(() => {
    if (!category || !zone) return []
    return category.materials.map(mt => {
      const material =  zone.pricePerMaterial?.find((mp) => mp.material === mt.name);

      return {
        material: material ? material.material : mt.name,
        price: material ? material.price : ''
      }
    })
  }, [zone, category])

  const isEmptyPrices = zone?.id && zone?.createdAt;

  const handleSubmit = async () => {
    if (!pickupZoneId || !dropOffZoneId || !vehicleCategoryId || !materialPrice?.length) {
      return message.error("Please fill all required fields.");
    }

    const hasEmptyPrices = materialsList?.some(mat => !mat?.price);
    if (hasEmptyPrices) {
      return message.error("Please enter a price for each material.");
    }

    try {

      if (isEmptyPrices) {
        await updateZone({
          pickupZoneId,
          id: zone?.id,
          dropoffZoneId: dropOffZoneId,
          pricePerMaterial: [
            ...materialPrice!
          ]
        })
        message.success("Zone mapping updated successfully")
      } else {
        await trigger({
          pickupZoneId,
          dropoffZoneId: dropOffZoneId,
          vehicleCategoryId,
          pricePerMaterial: [
            ...materialPrice!
          ]
        })
        
        message.success("Zone mapped successfully")        
      }
      mutate();
      onClose();
    } catch (error) {
      requestErrorHandler(error)
    }
  }

  if (!category) {
    return <PageSpinner />
  }

  return (
    <div className="min-h-full flex flex-col">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl text-primary font-medium">Set zone price</h2>
          <button onClick={onClose} className=" bg-transparent text-red-500 hover:text-red-700 border-none">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div >
            <label className="text-gray-500 text-sm block mb-2 w-full">From</label>
            <div className="border border-l ">
              <div className="w-full text-sm h-10 flex items-center item-center bg-blue-50 px-2 !border-2 !border-black rounded-none">
                {zone?.pickupZone.title || ""}
              </div>
            </div>
          </div>
          <div>
            <label className="text-gray-500 text-sm block mb-2">To</label>
            <div className="w-full h-10 flex items-center  text-sm bg-blue-50  px-2  border border-primary rounded-none">
              {zone?.dropoffZone.title || ""}
            </div>
          </div>
          <div className="mt-8 w-full overflow-hidden">
            <h3 className="text-sm opacity-50">Set Material Price(s)</h3>
            {
              materialsList?.map((material, index) => (
                <div className="mb-4" key={material.material}>
                  <label className="text-gray-500 text-sm block mb-2 capitalize ">{material.material}</label>
                  <div>
                    <Input
                      value={material.price}
                      onChange={(e) => {
                        if (isNaN(Number(e.target.value))) {
                          message.error("Please enter a valid number for the price.");
                          return;
                        }
                        const mat: MaterialPrice[] = [...materialsList!];
                        mat[index].material = material.material;
                        mat[index].price = e.target.value;
                        setMaterialPrice([...mat])
                      }}
                      className="font-bold bg-blue-50 px-2 rounded-md"
                    />
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="mt-8 p-6 space-y-3">
        <Button disabled={isMutating} onClick={() => {
          handleSubmit()
        }} className="w-full text-white bg-primary hover:bg-primary">
          {(isMutating || isUpdatingZone)
            ? <Loader className="animate-spin" />
            : isEmptyPrices ? "Update" : "Save"
          }
        </Button>
        <Button variant="outline" className="w-full" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
